import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { profileService } from "services/profileService";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function useOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState({ totalElements: 0, totalPages: 1 });

  const list = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await profileService.organizations.list(params);
      const content = Array.isArray(res?.content) ? res.content : [];
      setOrganizations(content);
      setPage({
        totalElements: res?.totalElements ?? content.length,
        totalPages: res?.totalPages ?? 1,
      });
      return res;
    } catch (err) {
      const message = toMessage(err, "Failed to load organizations");
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (payload, actorId) => {
    try {
      const res = await profileService.organizations.create(payload, actorId);
      toast.success("Organization created");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to create organization"));
      throw err;
    }
  }, []);

  const update = useCallback(async (id, payload, actorId) => {
    try {
      const res = await profileService.organizations.update(id, payload, actorId);
      toast.success("Organization updated");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to update organization"));
      throw err;
    }
  }, []);

  const remove = useCallback(async (id, actorId) => {
    try {
      await profileService.organizations.delete(id, actorId);
      toast.success("Organization removed");
    } catch (err) {
      toast.error(toMessage(err, "Failed to remove organization"));
      throw err;
    }
  }, []);

  return { organizations, loading, error, page, list, create, update, remove };
}
